// Considered: TypeFieldRowMolecule — rejected because that renders a labelled form field; an
// intent here is a selectable commitment (name, needsName/gated chips, priority pill, proposed
// accept/discard) inside a grid cell, not a field. See spec Reuse Audit (arc-editor-canvas-v1.md).
import React, { useState } from 'react';
import { useTranslation } from 'localization';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import TagAtom from '../../atoms/Tag/TagAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import { BLOCK_DRAG_MIME } from '../BlockPalette/BlockPaletteMolecule';
import './ArcIntentRowMolecule.scss';

export type ArcIntentRowPriority = 'high' | 'medium' | 'low';

export interface ArcIntentRowIntent {
  id: string;
  name: string;
  /** The assistant could not phrase this as a done state — the person names it themselves. */
  needsName?: boolean;
  /** Has non-empty prerequisites (`!cdIsEmpty(i.prerequisites)`). */
  gated?: boolean;
  /** At least one prerequisite the platform cannot evaluate (`cdUnsupported(i.prerequisites).length`). */
  gatedUnsupported?: boolean;
  gatedTitle?: string;
  /** Absent for a proposed intent — priority is authored on acceptance. */
  priority?: ArcIntentRowPriority;
  /** A proposal is the intent itself, drawn as unaccepted — not a preview. */
  proposed?: boolean;
  selected?: boolean;
}

export interface ArcIntentRowMoleculeProps {
  intents: ArcIntentRowIntent[];
  editable?: boolean;
  onSelect: (intent: ArcIntentRowIntent) => void;
  onAdd: () => void;
  onAcceptIntent: (intentId: string) => void;
  onDiscardIntent: (intentId: string) => void;
  /** Fired on drop with the dragged block id — the caller decides what "block" means. */
  onDropBlock?: (blockId: string) => void;
  className?: string;
}

const priorityTone: Record<ArcIntentRowPriority, 'danger' | 'warn' | 'neutral'> = {
  high: 'danger',
  medium: 'warn',
  low: 'neutral',
};

/** The intent lane cell for one stage x lane: one card per intent (name, needsName/gated chips,
 * priority pill, proposed accept/discard) or a "no intents here" hint, a drop target for palette
 * blocks, plus an add-intent mini button (`arc-lane.jsx:184-244`). */
const ArcIntentRowMolecule: React.FC<ArcIntentRowMoleculeProps> = ({
  intents,
  editable = false,
  onSelect,
  onAdd,
  onAcceptIntent,
  onDiscardIntent,
  onDropBlock,
  className = '',
}) => {
  const { t } = useTranslation();
  const [over, setOver] = useState(false);

  return (
    <div
      className={`arc-intent-row ${className}`.trim()}
      data-container="each(intents)"
      data-over={over ? 'true' : undefined}
      onDragOver={(e) => {
        if (!editable) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const blockId = e.dataTransfer.getData(BLOCK_DRAG_MIME);
        if (blockId && editable) onDropBlock?.(blockId);
      }}
    >
      {intents.length === 0 && (
        <span
          className="arc-intent-row__none"
          data-datum="canvas.no-intents-here"
          data-kind="hint"
        >
          {t('studio.arcEditor.intentRow.noIntentsHere')}
        </span>
      )}
      {intents.map((intent) => (
        // The card is a group, not the button: a proposed intent carries Keep/Discard buttons
        // beside its select button, and a button may not contain other interactive content.
        <div
          key={intent.id}
          className="arc-intent-row__card"
          data-proposed={intent.proposed ? 'true' : undefined}
          data-selected={intent.selected ? 'true' : undefined}
          data-datum="canvas.card#5"
          data-kind="card"
        >
          <button
            type="button"
            className="arc-intent-row__select"
            aria-pressed={intent.selected ? true : undefined}
            onClick={() => onSelect(intent)}
          >
          <GlyphAtom glyph={intent.proposed ? 'lucide:sparkles' : 'lucide:circle'} aria-hidden />
          <span className="arc-intent-row__name" data-datum="canvas.i-name" data-kind="value">
            {intent.name}
          </span>
          {intent.needsName && (
            <TagAtom
              label={t('studio.arcEditor.intentRow.nameIt')}
              tone="warn"
              size="small"
              title={t('studio.arcEditor.intentRow.nameItTitle')}
              data-datum="canvas.name-it"
              data-kind="chip"
            />
          )}
          {intent.gated && (
            <TagAtom
              label={t('studio.arcEditor.intentRow.gated')}
              icon="lucide:lock-keyhole"
              tone={intent.gatedUnsupported ? 'warn' : undefined}
              size="small"
              title={intent.gatedTitle}
              data-datum="canvas.gated"
              data-kind="chip"
            />
          )}
          {!intent.proposed && intent.priority && (
            <span
              className="arc-intent-row__prio"
              data-priority={intent.priority}
              data-datum="canvas.pill"
              data-kind="pill"
            >
              <TagAtom
                label={t(`studio.arcEditor.intentRow.priority.${intent.priority}`)}
                size="small"
                shape="pill"
                tone={priorityTone[intent.priority]}
                data-datum="canvas.chip#7"
                data-kind="chip"
              />
            </span>
          )}
          </button>
          {intent.proposed && editable && (
            <span className="arc-intent-row__propose-acts">
              <ButtonAtom
                variant="text"
                size="small"
                shape="icon-square"
                icon={<GlyphAtom glyph="lucide:check" aria-hidden />}
                title={t('studio.arcEditor.intentRow.keepIntent')}
                aria-label={t('studio.arcEditor.intentRow.keepIntentAria', { name: intent.name })}
                onClick={() => onAcceptIntent(intent.id)}
              />
              <ButtonAtom
                variant="text"
                size="small"
                shape="icon-square"
                icon={<GlyphAtom glyph="lucide:x" aria-hidden />}
                title={t('studio.arcEditor.intentRow.discardIntent')}
                aria-label={t('studio.arcEditor.intentRow.discardIntentAria', { name: intent.name })}
                onClick={() => onDiscardIntent(intent.id)}
              />
            </span>
          )}
        </div>
      ))}
      {editable && (
        <ButtonAtom
          variant="secondary"
          size="small"
          icon={<GlyphAtom glyph="lucide:plus" aria-hidden />}
          label={t('studio.arcEditor.intentRow.addIntent')}
          onClick={onAdd}
          data-datum="canvas.intent#3"
          data-kind="button"
        />
      )}
    </div>
  );
};

ArcIntentRowMolecule.displayName = 'ArcIntentRowMolecule';

export default ArcIntentRowMolecule;
